import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useMutation } from '@tanstack/react-query';
import {
  Mic,
  MicOff,
  Volume2,
  MessageSquare,
  Send,
  Loader,
  HelpCircle,
  TrendingUp,
  DollarSign,
  Package,
  Users,
  Clock,
  AlertCircle,
  CheckCircle,
  BarChart,
  Activity,
  X
} from 'lucide-react';
import { voiceService } from '../../services/voiceService';
import { useWebSocket } from '../../contexts/WebSocketContext';
import toast from 'react-hot-toast';

interface VoiceResponse {
  text: string;
  data?: any;
  action?: string;
  confidence: number;
}

interface Suggestion {
  icon: React.ReactNode;
  text: string;
  query: string;
}

const VoiceAssistant: React.FC = () => {
  const { socket } = useWebSocket();
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [conversation, setConversation] = useState<Array<{
    type: 'user' | 'assistant';
    text: string;
    timestamp: Date;
    data?: any;
  }>>([]);
  const [inputValue, setInputValue] = useState('');
  const [showTranscript, setShowTranscript] = useState(false);
  
  const recognitionRef = useRef<any>(null);
  const synthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const conversationEndRef = useRef<HTMLDivElement>(null);

  // Query mutation
  const queryMutation = useMutation(
    (query: string) => voiceService.processQuery(query),
    {
      onSuccess: (response: VoiceResponse) => {
        handleResponse(response);
      },
      onError: () => {
        toast.error('Failed to process query');
        setIsProcessing(false);
      }
    }
  );

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onstart = () => {
        setIsListening(true);
        setShowTranscript(true);
      };

      recognition.onresult = (event: any) => {
        const current = event.resultIndex;
        const transcript = event.results[current][0].transcript;
        setTranscript(transcript);
      };

      recognition.onend = () => {
        setIsListening(false);
        if (transcript) {
          handleVoiceInput(transcript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        toast.error('Voice recognition error. Please try again.');
      };

      recognitionRef.current = recognition;
    } else {
      toast.error('Voice recognition not supported in this browser');
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, [transcript]);

  // WebSocket for real-time updates
  useEffect(() => {
    if (socket) {
      socket.on('voice:response', (response: VoiceResponse) => {
        handleResponse(response);
      });

      return () => {
        socket.off('voice:response');
      };
    }
  }, [socket]);

  // Auto-scroll conversation
  useEffect(() => {
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleVoiceInput = async (text: string) => {
    setTranscript('');
    setShowTranscript(false);
    
    // Add user message to conversation
    setConversation(prev => [...prev, {
      type: 'user',
      text,
      timestamp: new Date()
    }]);

    setIsProcessing(true);
    await queryMutation.mutateAsync(text);
  };

  const handleTextInput = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim()) return;

    const query = inputValue;
    setInputValue('');

    // Add user message to conversation
    setConversation(prev => [...prev, {
      type: 'user',
      text: query,
      timestamp: new Date()
    }]);

    setIsProcessing(true);
    await queryMutation.mutateAsync(query);
  };

  const handleResponse = (response: VoiceResponse) => {
    // Add assistant response to conversation
    setConversation(prev => [...prev, {
      type: 'assistant',
      text: response.text,
      timestamp: new Date(),
      data: response.data
    }]);

    // Speak the response
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(response.text);
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      synthesisRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    }

    setIsProcessing(false);

    // Handle any actions
    if (response.action) {
      handleAction(response.action, response.data);
    }
  };

  const handleAction = (action: string, data?: any) => {
    switch (action) {
      case 'navigate':
        if (data?.path) {
          window.location.href = data.path;
        }
        break;
      case 'showChart':
        // Chart will be displayed in conversation
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
    } else {
      recognitionRef.current?.start();
    }
  };

  const stopSpeaking = () => {
    window.speechSynthesis.cancel();
    setIsSpeaking(false);
  };

  const suggestions: Suggestion[] = [
    {
      icon: <TrendingUp className="h-5 w-5" />,
      text: "What's today's revenue?",
      query: "What is today's revenue?"
    },
    {
      icon: <Package className="h-5 w-5" />,
      text: "Check parts inventory",
      query: "Show me parts inventory status"
    },
    {
      icon: <Users className="h-5 w-5" />,
      text: "Staff productivity",
      query: "How is staff productivity today?"
    },
    {
      icon: <DollarSign className="h-5 w-5" />,
      text: "Weekly KPI report",
      query: "Give me this week's KPI report"
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="h-full flex flex-col"
    >
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">TOP Voice Assistant</h1>
            <p className="text-blue-100">Ask me anything about your store performance</p>
          </div>
          <div className="flex items-center space-x-2">
            <div className={`h-3 w-3 rounded-full ${isProcessing ? 'bg-yellow-400 animate-pulse' : 'bg-green-400'}`} />
            <span className="text-sm">{isProcessing ? 'Processing...' : 'Ready'}</span>
          </div>
        </div>
      </div>

      {/* Conversation Area */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        {conversation.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">Start a conversation</h3>
            <p className="text-gray-500 mb-8">Ask me about your store's performance, KPIs, or any other questions</p>
            
            {/* Suggestions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setInputValue(suggestion.query);
                    handleTextInput({ preventDefault: () => {} } as React.FormEvent);
                  }}
                  className="flex items-center space-x-3 p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow text-left"
                >
                  <div className="text-blue-600">{suggestion.icon}</div>
                  <span className="text-sm font-medium text-gray-700">{suggestion.text}</span>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 max-w-3xl mx-auto">
            {conversation.map((message, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-lg ${message.type === 'user' ? 'order-2' : 'order-1'}`}>
                  <div className={`rounded-lg p-4 ${
                    message.type === 'user' 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white shadow-sm'
                  }`}>
                    <p className={message.type === 'user' ? 'text-white' : 'text-gray-800'}>
                      {message.text}
                    </p>
                    
                    {/* Display any data visualizations */}
                    {message.data?.chart && (
                      <div className="mt-4 bg-gray-50 rounded p-4">
                        <BarChart className="h-32 w-full text-gray-400" />
                        <p className="text-xs text-gray-500 mt-2 text-center">Chart visualization</p>
                      </div>
                    )}
                    
                    {message.data?.metrics && (
                      <div className="mt-4 grid grid-cols-2 gap-2">
                        {Object.entries(message.data.metrics).map(([key, value]: [string, any]) => (
                          <div key={key} className="bg-gray-50 rounded p-2">
                            <p className="text-xs text-gray-500">{key}</p>
                            <p className="font-semibold text-gray-800">{value}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <p className={`text-xs mt-1 ${
                    message.type === 'user' ? 'text-right text-gray-600' : 'text-gray-500'
                  }`}>
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </p>
                </div>
              </motion.div>
            ))}
            <div ref={conversationEndRef} />
          </div>
        )}
      </div>

      {/* Voice Transcript */}
      {showTranscript && transcript && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute bottom-32 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-75 text-white px-6 py-3 rounded-lg max-w-md"
        >
          <p className="text-sm">{transcript}</p>
        </motion.div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t p-6">
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleTextInput} className="flex items-center space-x-4">
            <button
              type="button"
              onClick={toggleListening}
              disabled={isProcessing}
              className={`p-4 rounded-full transition-all ${
                isListening 
                  ? 'bg-red-600 hover:bg-red-700 animate-pulse' 
                  : 'bg-blue-600 hover:bg-blue-700'
              } text-white disabled:opacity-50`}
            >
              {isListening ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </button>

            <div className="flex-1 relative">
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder="Type your question or click the mic to speak..."
                disabled={isProcessing || isListening}
                className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              />
              {isSpeaking && (
                <button
                  type="button"
                  onClick={stopSpeaking}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={isProcessing || !inputValue.trim()}
              className="p-4 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {isProcessing ? (
                <Loader className="h-6 w-6 animate-spin" />
              ) : (
                <Send className="h-6 w-6" />
              )}
            </button>
          </form>

          <div className="flex items-center justify-center mt-4 space-x-4 text-sm text-gray-500">
            {isSpeaking && (
              <div className="flex items-center space-x-2">
                <Volume2 className="h-4 w-4 animate-pulse" />
                <span>Speaking...</span>
              </div>
            )}
            {isProcessing && (
              <div className="flex items-center space-x-2">
                <Activity className="h-4 w-4 animate-pulse" />
                <span>AI Processing...</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default VoiceAssistant;